import cn from 'classnames'
import React from 'react'

import styles from './IntegrationButton.module.scss'

import { useIsFocusedByKeyboard } from '~shared/lib/useIsFocusedByKeyboard'
import Spinner from '~shared/ui/Spinner'
import Text from '~shared/ui/Text'

interface IntegrationButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: string | JSX.Element
  icon?: JSX.Element
  loading?: boolean
  loadingText?: string
}

const defaultLoadingText = 'Getting your data! This may take up to 10 minutes if there’s a lot.'

function IntegrationButton({
  label,
  icon,
  disabled,
  loading,
  loadingText = defaultLoadingText,
  className,
  onFocus,
  onBlur,
  onMouseUp,
  onMouseDown,
  ...props
}: IntegrationButtonProps) {
  const isDisplayLoadingText = loading && loadingText
  const isDisplayIcon = icon && !loading
  const isDisplayAside = icon || loading

  const { isFocusedByKeyboard, ...focusHandlers } = useIsFocusedByKeyboard({
    onFocus,
    onBlur,
    onMouseUp,
    onMouseDown,
  })

  return (
    <button
      type="button"
      disabled={disabled}
      className={cn(
        styles.root,
        isFocusedByKeyboard && styles.focusedByKeyboard,
        disabled && styles.disabled,
        className
      )}
      {...props}
      {...focusHandlers}
    >
      <div className={styles.main}>
        <span className={styles.label}>{label}</span>

        {isDisplayLoadingText && (
          <Text variant="secondary" textType="caption" className="mt-0-5">
            {loadingText}
          </Text>
        )}
      </div>

      {isDisplayAside && (
        <div className={styles.aside}>
          {isDisplayIcon && <span className={styles.icon}>{icon}</span>}
          {loading && <Spinner size="sm" variant="accent" />}
        </div>
      )}
    </button>
  )
}

export default React.memo(IntegrationButton)
